import dayjs from 'dayjs';

import {
  Document,
  DocumentProcessingStatus,
  ExternalEhrDocument,
  LeadConnectorEnum,
  PdfConversionStatus,
  ScreenerDocumentError,
  ScreenerDocumentErrorErrorTypeEnum,
  User
} from '@/types/crm-screener';
import { getFullName } from '@/utils/getFullName';

import { getDocumentDisplayName } from '../utils/documentDisplayName';

export const MAX_DOCUMENT_NAME_LENGTH = 100;

export enum DocumentStatus {
  ERROR = 'ERROR',
  RECOVERED = 'RECOVERED',
  REANALYZED = 'REANALYZED',
  UNPROCESSABLE = 'UNPROCESSABLE',
  NORMAL = 'NORMAL',
  NEW = 'NEW'
}

const connectorsWithoutUploadAttribution: LeadConnectorEnum[] = [
  LeadConnectorEnum.ManualUpload,
  LeadConnectorEnum.ManualCopy,
  LeadConnectorEnum.Roc
];

export class DocumentModel implements Document {
  id?: string | undefined;

  lead_id?: string | undefined;
  name: string;
  display_name?: string | null;
  s3_key: string;
  gcs_key?: string | undefined;
  s3_access_url?: string | undefined;

  processing_status?: DocumentProcessingStatus | undefined;
  connector_document_id?: string | undefined;
  errors?: ScreenerDocumentError[] | undefined;
  processing_error?: string | undefined;
  page_count?: number | undefined;
  filesize?: number | undefined;

  pdf_conversion_status?: PdfConversionStatus | undefined;
  orig_s3_key?: string | undefined;
  orig_content_hash?: string | undefined;
  content_type?: string | undefined;
  has_been_analyzed?: boolean | undefined;
  metadata?: { [key: string]: any } | undefined;
  is_failed_ocr?: boolean | undefined;
  created_by_user?: User | undefined;
  external_ehr_document?: ExternalEhrDocument | undefined;
  combined_from_lead_id?: string | undefined;

  created_at?: string | undefined;

  constructor(payload: Document) {
    Object.assign(this, payload);
  }

  public getDisplayName(): string {
    return getDocumentDisplayName(this);
  }

  public getStatus(): DocumentStatus {
    if (
      this.errors?.some(
        (e) =>
          e.error_type === ScreenerDocumentErrorErrorTypeEnum.ProcessingError
      ) ||
      this.processing_error
    ) {
      return DocumentStatus.UNPROCESSABLE;
    }

    if (!this.s3_key) {
      return DocumentStatus.ERROR;
    }

    const allErrorsRecovered =
      this.errors &&
      this.errors.length > 0 &&
      this.errors.every((err) => err.recovered_at);

    if (this.has_been_analyzed === false) {
      if (allErrorsRecovered) {
        return DocumentStatus.RECOVERED;
      }
      if (this.errors && this.errors.length > 0) {
        return DocumentStatus.ERROR;
      }
      return DocumentStatus.NEW;
    }

    return DocumentStatus.NORMAL;
  }

  public getUploadDetailText(leadConnector?: LeadConnectorEnum): string {
    const uploadText = this.combined_from_lead_id
      ? 'Uploaded from combined referrals'
      : `Uploaded on ${dayjs(this.created_at).format('MMMM D [at] h:mm A')}`;

    if (this.created_by_user) {
      return `${uploadText} by ${getFullName(this.created_by_user)}`;
    }

    if (
      leadConnector &&
      !connectorsWithoutUploadAttribution.includes(leadConnector)
    ) {
      return `${uploadText} by ${leadConnector}`;
    }

    return uploadText;
  }
}
